import { forwardRef, useEffect } from 'react'
import { ModalContext } from './ModalContext'

/**
 * Composant Modal réutilisable utilisant DaisyUI
 * 
 * @param {Object} props - Les props du composant
 * @param {boolean} props.open - État d'ouverture de la modale
 * @param {Function} props.onClose - Fonction à exécuter lors de la fermeture
 * @param {ReactNode} props.children - Contenu de la modale
 * @param {string} props.dataTheme - Thème de données à appliquer
 * @param {string} props.className - Classes CSS additionnelles pour la modal-box
 * @param {string} props.position - Position de la modale (top, middle, bottom, start, end)
 * @param {boolean} props.responsive - Si true, modale en bas sur mobile et au milieu sur desktop
 * @param {boolean} props.backdrop - Afficher l'arrière-plan avec fermeture au clic
 * @param {boolean} props.ariaHidden - Si la modale est cachée pour les lecteurs d'écran
 * @returns {JSX.Element} Le composant Modal
 */
const Modal = forwardRef(({
  open = false,
  onClose,
  children,
  dataTheme,
  className = '',
  position = 'middle',
  responsive = false,
  backdrop = true,
  ariaHidden,
  ...props
}, ref) => {
  // Définir ariaHidden basé sur l'état d'ouverture si non fourni
  ariaHidden = ariaHidden ?? !open

  // Définir les classes de position
  const positionClasses = {
    top: 'modal-top',
    middle: 'modal-middle',
    bottom: 'modal-bottom',
    start: 'modal-start',
    end: 'modal-end'
  }
  
  // Construire la classe de conteneur
  let containerClasses = 'modal'
  
  if (open) {
    containerClasses += ' modal-open'
  }
  
  if (position && positionClasses[position]) {
    containerClasses += ` ${positionClasses[position]}`
  }
  
  if (responsive) {
    containerClasses += ' modal-bottom sm:modal-middle'
  }

  // Gérer le verrouillage du scrolling du body
  useEffect(() => {
    if (open) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
    
    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [open])

  // Gérer la fermeture via la touche Escape
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && onClose) {
      onClose()
    }
  }

  return (
    <ModalContext.Provider value={{ onClose, isOpen: open }}>
      <dialog
        {...props}
        ref={ref}
        className={containerClasses}
        // onKeyDown={handleKeyDown}
        aria-label="Modal"
        aria-hidden={ariaHidden}
        open={open}
        aria-modal={open}
        data-theme={dataTheme}
      >
        <div data-theme={dataTheme} className={`modal-box ${className}`}>
          {children}
        </div>
        
        {/* Arrière-plan cliquable pour fermer la modale */}
        {backdrop && (
          <form method="dialog" className="modal-backdrop">
            <button onClick={onClose}>close</button>
          </form>
        )}
      </dialog>
    </ModalContext.Provider>
  )
})

// Définir un displayName pour les DevTools
Modal.displayName = 'Modal'

// Modal.propTypes = {
//   open: PropTypes.bool,
//   onClose: PropTypes.func,
//   children: PropTypes.node,
//   dataTheme: PropTypes.string,
//   className: PropTypes.string,
//   position: PropTypes.oneOf(['top', 'middle', 'bottom', 'start', 'end']),
//   responsive: PropTypes.bool,
//   backdrop: PropTypes.bool,
//   ariaHidden: PropTypes.bool
// }

export default Modal